export interface MapEvent {
  type: string;
  target: any;
}

export interface MapPointerEvent extends MapEvent {
  coordinate: [number, number];
  pixel: [number, number];
  originalEvent: PointerEvent | MouseEvent;
  dragging: boolean;
}

export interface LayerEvent extends MapEvent {
  layer: any;
}

export interface FeatureEvent {
  type: string;
  feature: any;
  layer: any;
  coordinate?: [number, number];
}

export interface RenderEvent extends MapEvent {
  context: CanvasRenderingContext2D | WebGL2RenderingContext;
  frameState: FrameState;
}

export interface ErrorEvent extends MapEvent {
  error: Error;
  layer?: any;
  source?: any;
}

export interface FrameState {
  time: number;
  viewState: {
    center: [number, number];
    zoom: number;
    rotation: number;
    resolution: number;
  };
  extent: [number, number, number, number];
  size: [number, number];
  pixelRatio: number;
}

export type EventHandler = (...args: any[]) => void;
export type EventKey = { type: string; handler: EventHandler; layerId?: string };

interface ScopedEntry {
  handler: EventHandler;
  layerId?: string;
}

export class EventEmitter {
  private listeners: Map<string, Set<ScopedEntry>> = new Map();

  /**
   * Register a handler. Two signatures (mirrors MapLibre):
   *   on(event, handler)
   *   on(event, layerId, handler)   ← only fires if event payload's `layer.id` matches
   */
  on(event: string, layerIdOrHandler: string | EventHandler, maybeHandler?: EventHandler): EventKey {
    const layerId = typeof layerIdOrHandler === 'string' ? layerIdOrHandler : undefined;
    const handler = typeof layerIdOrHandler === 'string' ? (maybeHandler as EventHandler) : layerIdOrHandler;
    if (!this.listeners.has(event)) this.listeners.set(event, new Set());
    this.listeners.get(event)!.add({ handler, layerId });
    return { type: event, handler, layerId };
  }

  off(event: string, layerIdOrHandler: string | EventHandler, maybeHandler?: EventHandler): void {
    const layerId = typeof layerIdOrHandler === 'string' ? layerIdOrHandler : undefined;
    const handler = typeof layerIdOrHandler === 'string' ? (maybeHandler as EventHandler) : layerIdOrHandler;
    const set = this.listeners.get(event);
    if (!set) return;
    for (const entry of set) {
      if (entry.handler === handler && entry.layerId === layerId) {
        set.delete(entry);
        return;
      }
    }
  }

  once(event: string, layerIdOrHandler: string | EventHandler, maybeHandler?: EventHandler): EventKey {
    const layerId = typeof layerIdOrHandler === 'string' ? layerIdOrHandler : undefined;
    const handler = typeof layerIdOrHandler === 'string' ? (maybeHandler as EventHandler) : layerIdOrHandler;
    const wrapper = (...args: any[]) => {
      handler(...args);
      if (layerId) this.off(event, layerId, wrapper as any);
      else this.off(event, wrapper as any);
    };
    if (layerId) return this.on(event, layerId, wrapper);
    return this.on(event, wrapper);
  }

  dispatchEvent(event: MapEvent): boolean {
    const set = this.listeners.get(event.type);
    if (!set || set.size === 0) return false;
    for (const entry of set) {
      if (entry.layerId && (event as any)?.layer?.id !== entry.layerId) continue;
      entry.handler(event);
    }
    return true;
  }

  protected emit(type: string, data?: Record<string, any>): void {
    const set = this.listeners.get(type);
    if (!set) return;
    const event = { type, target: this, ...data };
    for (const entry of set) {
      if (entry.layerId && (event as any)?.layer?.id !== entry.layerId) continue;
      entry.handler(event);
    }
  }

  hasListeners(event: string): boolean {
    const set = this.listeners.get(event);
    return !!set && set.size > 0;
  }

  removeAllListeners(): void {
    this.listeners.clear();
  }
}
